// The behaviour coach: puts his live state on top of the general advice, so
// "he's bitey" at 7pm after an hour awake gets "he's overtired — nap time",
// not a lecture on bite inhibition; and once a few spells are logged, it
// spots when they happen ("usually after about 50 minutes awake, 18:00–20:00").

import type { BehaviourId } from '../content/behaviour.ts';
import { FIGURES, cite } from '../content/sources.ts';
import type { SourceRef } from '../content/types.ts';
import { awakeWindowMinutes, toiletIntervalMinutes } from './age.ts';
import { DAY, MINUTE } from './time.ts';
import type { LogEvent, Millis } from './types.ts';

/** The behaviours that are mostly about energy and tiredness. */
const AROUSAL: BehaviourId[] = ['bitey', 'zoomies', 'wont-settle', 'humping', 'lead-biting', 'jumping'];

export interface CoachContext {
  ageWeeks: number;
  asleep: boolean;
  awakeFor?: Millis;
  lastWee?: Millis;
  now: Millis;
  /** "crate" or "pen": where he sleeps. */
  den?: string;
  /** How long his last nap lasted, if it's logged (minutes). */
  lastNapMin?: number;
}

/**
 * The first thing to know, for this behaviour, right now — or undefined when
 * there's nothing in his state that changes the general advice.
 */
export function coachNow(id: BehaviourId, c: CoachContext): { tone: 'warn' | 'info'; text: string; sources?: SourceRef[] } | undefined {
  const window = awakeWindowMinutes(c.ageWeeks);
  const awake = c.awakeFor !== undefined ? Math.round(c.awakeFor / MINUTE) : undefined;
  const sinceWee = c.lastWee !== undefined ? Math.round((c.now - c.lastWee) / MINUTE) : undefined;
  const hour = new Date(c.now).getHours();

  if (id === 'crate-crying') {
    if ((hour >= 22 || hour < 7) && sinceWee !== undefined && sinceWee >= 120) return { tone: 'warn', text: `It’s been ${hours(sinceWee)} since his last wee — he may genuinely need to go. Carry him out, cue word, straight back to bed.`, sources: cite('pdsaNightCry', 'rkcToilet') };
    return undefined;
  }
  if (id === 'pen') {
    // Tired, or just not ready to be on his own yet? The awake clock tells them apart.
    if (awake !== undefined && awake >= window * 0.75)
      return { tone: 'warn', text: `He’s been up ${awake} minutes — at ${c.ageWeeks} weeks that’s near his limit, so this is likely tiredness, not the pen. Toilet, then his ${c.den ?? 'crate'} for a nap with a chew, lights low.`, sources: [...FIGURES.awakeWindow.sources] };
    if (awake !== undefined && awake < window * 0.5)
      return { tone: 'info', text: `He’s only been up ${awake} minutes, so this probably isn’t tiredness — he wants to be with you, and being on his own is still new. Let him out at the first second of quiet, then try again with a stuffed Kong and you in sight — and when he starts to slow down, it’s nap time in his ${c.den ?? 'crate'}.`, sources: cite('rkcAlone', 'rspcaAlone') };
    return undefined;
  }
  if (!AROUSAL.includes(id)) return undefined;
  if (awake !== undefined && awake >= window * 0.75)
    return { tone: 'warn', text: `He’s been awake ${awake} minutes — at ${c.ageWeeks} weeks, about ${window} is his limit. This is very likely overtiredness: toilet trip, then a nap in his ${c.den ?? 'crate'} with a chew.`, sources: [...FIGURES.awakeWindow.sources] };
  // A short nap leaves him still tired, however recently he woke.
  if (c.lastNapMin !== undefined && c.lastNapMin < 40 && awake !== undefined && awake < window * 0.75)
    return { tone: 'warn', text: `His last nap was only ${c.lastNapMin} minutes — he’s probably still tired. Quick wee, then back in his ${c.den ?? 'crate'} with a chew, lights low.`, sources: [...FIGURES.awakeWindow.sources] };
  if (sinceWee !== undefined && sinceWee >= toiletIntervalMinutes(c.ageWeeks)) return { tone: 'info', text: `It’s been ${hours(sinceWee)} since his last wee — a toilet trip first may take the edge off.`, sources: [...FIGURES.toiletInterval.sources] };
  if (awake !== undefined && awake < 10)
    return { tone: 'info', text: `He’s only just woken up — straight out for a wee, then something calm to chew or sniff. Still wild after a few minutes? That nap probably wasn’t long enough: back in his ${c.den ?? 'crate'} with a chew.` };
  if (id === 'bitey' && hour >= 17 && hour < 21) return { tone: 'info', text: `Early evening is the classic “witching hour”. A wee and a nap in his ${c.den ?? 'crate'} now, and a proper nap around 5–6pm tomorrow before it starts, usually helps.`, sources: cite('dtMouthing', 'dtPuppyProblems') };
  return undefined;
}

const hours = (min: number) => (min < 90 ? `${min} minutes` : `${Math.round((min / 60) * 2) / 2} hours`);

export interface Pattern {
  count: number;
  /** Typical minutes awake when it starts (if we know his sleeps). */
  awakeMinutes?: number;
  /** "18:00–20:00", when most spells fall in a two-hour slot. */
  window?: string;
  advice: string;
}

/**
 * When a behaviour tends to happen, from the spells logged in the last two
 * weeks (needs three). Awake time is measured from the end of the sleep before.
 */
export function behaviourPattern(kind: string, events: LogEvent[], now: Millis, ageWeeks: number): Pattern | undefined {
  const spells = events.filter((e): e is LogEvent<'behaviour'> => e.type === 'behaviour' && e.data.kind === kind && e.at > now - 14 * DAY && e.at <= now);
  if (spells.length < 3) return undefined;
  const sleeps = events.filter((e): e is LogEvent<'sleep'> => e.type === 'sleep' && e.end !== undefined);
  const awake = spells
    .map((s) => {
      const before = sleeps.filter((z) => z.end! <= s.at && s.at - z.end! < 4 * 60 * MINUTE).sort((a, b) => b.end! - a.end!)[0];
      return before ? (s.at - before.end!) / MINUTE : undefined;
    })
    .filter((m): m is number => m !== undefined)
    .sort((a, b) => a - b);
  const awakeMinutes = awake.length >= 3 ? Math.round(awake[Math.floor(awake.length / 2)] / 5) * 5 : undefined;

  // The two-hour slot (starting on any hour) holding most spells, if it's at least half of them.
  let best = { start: 0, n: 0 };
  for (let h = 0; h < 24; h++) {
    const n = spells.filter((s) => {
      const hr = new Date(s.at).getHours();
      return hr === h || hr === (h + 1) % 24;
    }).length;
    if (n > best.n) best = { start: h, n };
  }
  const window = best.n >= Math.max(2, spells.length / 2) ? `${pad(best.start)}:00–${pad((best.start + 2) % 24)}:00` : undefined;

  const limit = awakeWindowMinutes(ageWeeks);
  const parts: string[] = [];
  if (awakeMinutes !== undefined) parts.push(`usually after about ${awakeMinutes} minutes awake`);
  if (window) parts.push(`mostly ${window}`);
  const when = parts.length ? `${parts.join(', ')}.` : '';
  const advice =
    awakeMinutes !== undefined && awakeMinutes >= limit * 0.75
      ? `${when} That’s at the end of what he can manage — aim for a nap by ${Math.max(30, awakeMinutes - 10)} minutes${window ? `, especially ${window}` : ''}.`
      : window
        ? `${when} Plan a calm game and a nap just before ${window.split('–')[0]}.`
        : when || 'Keep logging — a pattern usually shows within a week.';
  return { count: spells.length, awakeMinutes, window, advice };
}

const pad = (n: number) => String(n).padStart(2, '0');
